import { getDepRetFlightPairs } from "./vacationPackagesLinkedFlights";
import {
  isHrInternalBase64JsonDealToken,
  isLdsOpaqueDealToken,
  pickLdsOpaqueDealToken,
} from "./lds/ldsDealTokenSsot";

const TRACE_TAG = "[VACATION_PACKAGE_FLIGHT_SELECTION_TRACE]";

function isDevRuntime() {
  try {
    return Boolean(import.meta?.env?.DEV);
  } catch {
    return false;
  }
}

function toArray(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value == null) return [];
  return [value];
}

function normalizeLeg(legLike) {
  if (!legLike) return null;
  if (Array.isArray(legLike)) return legLike[0] || null;
  return legLike;
}

function normalizeFlightNode(flightNodeLike) {
  if (!flightNodeLike) return null;
  const node = Array.isArray(flightNodeLike) ? flightNodeLike[0] : flightNodeLike;
  if (!node || typeof node !== "object") return null;
  return node;
}

function stripStrayTimeAffixes(raw) {
  if (raw == null) return "";
  return String(raw)
    .trim()
    .replace(/(\d{2}:\d{2})\s*T\s*$/i, "$1")
    .replace(/\s*T\s*$/i, "");
}

/**
 * Canonical datetime token for flight-option signatures (aligned with backend normalization).
 */
export function normalizeFlightDateTimeForSignature(raw) {
  if (raw == null) return "";
  let s = String(raw).trim();
  if (!s) return "";
  if (s.toUpperCase() === "NA") return s;

  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const trimmed = s.trim();
    const explicitUtc = /Z$/i.test(trimmed) || /[+-]\d{1,2}:\d{2}(?::\d{2})?$/i.test(trimmed);
    const iso = s.replace(/Z$/i, "+00:00").replace(/^(\d{4}-\d{2}-\d{2})[T ](\d)/, "$1T$2");
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) {
      const pad = (n) => String(n).padStart(2, "0");
      if (explicitUtc) {
        return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ${pad(
          d.getUTCHours()
        )}:${pad(d.getUTCMinutes())}`;
      }
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(
        d.getMinutes()
      )}`;
    }
  }

  s = s.replace(/^(\d{1,2}\/\d{1,2}\/\d{4})T(\d{2}:\d{2})/, "$1 $2");
  const m = s.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?/
  );
  if (m && m[4] && m[5]) {
    return `${String(Number(m[1])).padStart(2, "0")}/${String(Number(m[2])).padStart(2, "0")}/${m[3]} ${m[4]}:${m[5]}`;
  }
  s = s.replace(/(\d{2}:\d{2})T\s*$/i, "$1");
  s = s.replace(/(\d{1,2}\/\d{1,2}\/\d{4})T\s*$/i, "$1");
  s = s.replace(/^(\d{1,2}\/\d{1,2}\/\d{4}\s+\d{2}:\d{2}):\d{2}$/, "$1");
  return s.replace(/\s+/g, " ").trim();
}

/** Normalize full "|...>>|..." signature (dep/arr datetime fields only). */
export function normalizeFlightSelectionSignature(signature) {
  if (signature == null) return "";
  const sig = String(signature).trim();
  if (!sig) return "";
  return sig
    .split(">>")
    .map((leg) => {
      const raw = leg.trim();
      const parts = raw.split("|");
      if (parts.length >= 6) {
        const copy = [...parts];
        copy[2] = normalizeFlightDateTimeForSignature(parts[2]);
        copy[3] = normalizeFlightDateTimeForSignature(parts[3]);
        return copy.join("|");
      }
      return raw;
    })
    .join(">>");
}

function combineDateTimeForLeg(dateValue, timeValue) {
  const d = dateValue != null ? stripStrayTimeAffixes(dateValue) : "";
  if (!d) return null;
  const t = timeValue != null ? stripStrayTimeAffixes(timeValue) : "";
  const hasClockInDate = /\d{1,2}\/\d{1,2}\/\d{4}\s+\d{2}:\d{2}/.test(d);
  if (hasClockInDate) {
    return d;
  }
  if (t) {
    return /\d{1,2}\/\d{1,2}\/\d{4}/.test(d) ? `${d} ${t}` : `${d}T${t}`;
  }
  const bareDateT = /^(\d{1,2}\/\d{1,2}\/\d{4})T\s*$/i.exec(d);
  if (bareDateT) {
    return bareDateT[1];
  }
  if (/\d{1,2}\/\d{1,2}\/\d{4}/.test(d) && !/\d{2}:\d{2}/.test(d) && d.endsWith("T")) {
    return d.slice(0, -1).trim();
  }
  return d;
}

function toIsoDateTime(dateValue, timeValue) {
  const combined = combineDateTimeForLeg(dateValue, timeValue);
  if (combined == null) return null;
  const n = normalizeFlightDateTimeForSignature(combined);
  return n || null;
}


function resolveLegSignaturePart(leg) {
  if (!leg || typeof leg !== "object") return "na";
  const airline =
    leg.airline ??
    leg.airLine ??
    leg.marketingAirline ??
    leg.carrier ??
    leg.supplierName ??
    "";
  const flightNumber = leg.flightNumber ?? leg.fltNo ?? "";
  const depDateTime = toIsoDateTime(leg.depDate, leg.depTime);
  const arrDateTime = toIsoDateTime(leg.arrDate, leg.arrTime);
  const origin = leg.depAirport ?? leg.origin ?? leg.from ?? "";
  const destination = leg.arrAirport ?? leg.destination ?? leg.to ?? "";
  return [
    String(airline || "").trim().toUpperCase(),
    String(flightNumber || "").trim().toUpperCase(),
    String(depDateTime || "").trim(),
    String(arrDateTime || "").trim(),
    String(origin || "").trim().toUpperCase(),
    String(destination || "").trim().toUpperCase(),
  ].join("|");
}

function resolveAirlineFromLeg(leg) {
  if (!leg || typeof leg !== "object") return null;
  const raw =
    leg.airline ??
    leg.airLine ??
    leg.marketingAirline ??
    leg.carrier ??
    leg.supplierName ??
    null;
  if (raw == null) return null;
  const out = String(raw).trim();
  return out || null;
}

function buildSelectedRoomMapping(selectedResult) {
  const selectedRoom = selectedResult?.selectedRoom;
  if (!selectedRoom || typeof selectedRoom !== "object") return null;
  return {
    roomId:
      selectedRoom.roomId ??
      selectedRoom.RoomId ??
      selectedRoom.roomID ??
      selectedRoom["@seqNum"] ??
      null,
    roomType: selectedRoom.roomType ?? selectedRoom.roomName ?? null,
    ldsDealId: selectedRoom.ldsDealId ?? null,
    raw: selectedRoom,
  };
}

export function normalizeLinkedFlightOptions(linkedFlightsCandidate) {
  if (Array.isArray(linkedFlightsCandidate)) {
    return linkedFlightsCandidate.filter((item) => item && typeof item === "object");
  }
  if (!linkedFlightsCandidate || typeof linkedFlightsCandidate !== "object") {
    return [];
  }
  const nestedFlights = linkedFlightsCandidate.flight;
  if (Array.isArray(nestedFlights)) {
    const looksLikeOptions = nestedFlights.some(
      (item) => item && typeof item === "object" && (item.flight || item.ldsDealId || item.dealId)
    );
    if (looksLikeOptions) {
      return nestedFlights.filter((item) => item && typeof item === "object");
    }
  }
  return [linkedFlightsCandidate];
}

export function buildFlightOptionSignature(linkedFlightOption) {
  if (!linkedFlightOption || typeof linkedFlightOption !== "object") return null;
  const flights = toArray(linkedFlightOption.flight);
  const outboundLeg = normalizeLeg(normalizeFlightNode(flights[0])?.leg);
  const inboundLeg = normalizeLeg(normalizeFlightNode(flights[1])?.leg);
  return [resolveLegSignaturePart(outboundLeg), resolveLegSignaturePart(inboundLeg)].join(">>");
}

function resolveOptionId(linkedFlightOption, optionSignature, selectedFlightIndex) {
  if (!linkedFlightOption || typeof linkedFlightOption !== "object") return null;
  const candidate = [
    linkedFlightOption.optionId,
    linkedFlightOption.flightOptionId,
    linkedFlightOption.ldsDealId,
    linkedFlightOption.dealId,
    linkedFlightOption["@seqNum"],
  ].find((value) => value != null && String(value).trim() !== "");
  if (candidate != null) return String(candidate).trim();
  if (selectedFlightIndex != null && Number.isFinite(Number(selectedFlightIndex))) {
    return `idx:${Number(selectedFlightIndex)}`;
  }
  return optionSignature || null;
}

function resolveSelectedOptionBySignatureOrIndex(pool, selectedFlight, selectedFlightIndex) {
  const options = normalizeLinkedFlightOptions(pool);
  const selectedSignature = buildFlightOptionSignature(selectedFlight);
  if (selectedSignature) {
    const normalizedSelected = normalizeFlightSelectionSignature(selectedSignature);
    const signatureMatch = options.find(
      (option) =>
        normalizeFlightSelectionSignature(buildFlightOptionSignature(option)) === normalizedSelected
    );
    if (signatureMatch) {
      return {
        option: signatureMatch,
        index: options.findIndex((x) => x === signatureMatch),
      };
    }
  }
  if (Number.isInteger(selectedFlightIndex) && selectedFlightIndex >= 0 && selectedFlightIndex < options.length) {
    return {
      option: options[selectedFlightIndex],
      index: selectedFlightIndex,
    };
  }
  if (selectedFlight && typeof selectedFlight === "object") {
    return {
      option: selectedFlight,
      index: Number.isInteger(selectedFlightIndex) ? selectedFlightIndex : null,
    };
  }
  return { option: null, index: null };
}

export function buildSelectedFlightOptionFromUser({
  selectedResult,
  selectedFlight,
  selectedFlightIndex,
  source = "user_selected",
  linkedFlightsPool,
}) {
  const pool = linkedFlightsPool ?? selectedResult?.linkedFlights ?? null;
  const { option, index } = resolveSelectedOptionBySignatureOrIndex(
    pool,
    selectedFlight,
    selectedFlightIndex
  );
  const defaultOptions = normalizeLinkedFlightOptions(pool);
  const resolvedOption =
    option ||
    (source === "default_first" && defaultOptions.length > 0 ? defaultOptions[0] : null);
  const resolvedIndex =
    index != null
      ? index
      : source === "default_first" && defaultOptions.length > 0
        ? 0
        : null;

  if (!resolvedOption) return null;

  const flights = toArray(resolvedOption.flight);
  const outboundLeg = normalizeLeg(normalizeFlightNode(flights[0])?.leg);
  const inboundLeg = normalizeLeg(normalizeFlightNode(flights[1])?.leg);
  const optionSignature = buildFlightOptionSignature(resolvedOption);
  const optionId = resolveOptionId(resolvedOption, optionSignature, resolvedIndex);
  const ldsDealId = pickLdsOpaqueDealToken(
    resolvedOption.ldsDealId,
    resolvedOption.dealId,
    selectedResult?.ldsDealId,
    selectedResult?.dealId
  );

  return {
    optionId,
    optionSignature,
    selectedFlightIndex: Number.isInteger(resolvedIndex) ? resolvedIndex : null,
    ldsDealId: ldsDealId != null ? String(ldsDealId) : null,
    linkedFlights: resolvedOption,
    outboundFlight: outboundLeg || null,
    inboundFlight: inboundLeg || null,
    airline: resolveAirlineFromLeg(outboundLeg),
    departureDateTime: toIsoDateTime(outboundLeg?.depDate, outboundLeg?.depTime),
    returnDateTime: toIsoDateTime(inboundLeg?.depDate, inboundLeg?.depTime),
    selectedRoomMapping: buildSelectedRoomMapping(selectedResult),
    source,
    isDefault: source === "default_first",
  };
}

export function applySelectedFlightOptionToSelectedResult(selectedResult, selectedFlightOption) {
  if (!selectedResult || typeof selectedResult !== "object") return selectedResult;
  if (!selectedFlightOption || typeof selectedFlightOption !== "object") return selectedResult;
  const hadComposeDealRefStamp = Object.prototype.hasOwnProperty.call(
    selectedResult,
    "__hrComposeDealRefPayload",
  );
  const composeDealRefStampValue = hadComposeDealRefStamp
    ? selectedResult.__hrComposeDealRefPayload
    : undefined;
  const next = {
    ...selectedResult,
    linkedFlights: selectedFlightOption.linkedFlights ?? selectedResult.linkedFlights ?? null,
    selectedFlightIndex:
      selectedFlightOption.selectedFlightIndex != null
        ? selectedFlightOption.selectedFlightIndex
        : selectedResult.selectedFlightIndex ?? null,
    selectedFlightOptionFromUser: selectedFlightOption,
  };
  const opaqueFlightToken = pickLdsOpaqueDealToken(selectedFlightOption.ldsDealId);
  if (opaqueFlightToken) {
    next.ldsDealId = opaqueFlightToken;
  }
  if (!next.selectedRoom && selectedFlightOption.selectedRoomMapping?.raw) {
    next.selectedRoom = selectedFlightOption.selectedRoomMapping.raw;
  }
  /* SSOT compose stamp is non-enumerable; object spread drops it — re-attach after merge */
  if (hadComposeDealRefStamp) {
    try {
      Reflect.deleteProperty(next, "__hrComposeDealRefPayload");
      Object.defineProperty(next, "__hrComposeDealRefPayload", {
        value: composeDealRefStampValue,
        enumerable: false,
        configurable: true,
        writable: true,
      });
    } catch (_) {
      try {
        next.__hrComposeDealRefPayload = composeDealRefStampValue;
      } catch {
        /* ignore */
      }
    }
  }
  return next;
}

export function ensureSelectedFlightOptionSSOT(selectedResult, { sourceIfMissing = "default_first" } = {}) {
  if (!selectedResult || typeof selectedResult !== "object") return selectedResult;
  const existing = selectedResult.selectedFlightOptionFromUser;
  if (existing && typeof existing === "object" && existing.linkedFlights) {
    const withDefaults = {
      ...existing,
      optionSignature: existing.optionSignature || buildFlightOptionSignature(existing.linkedFlights),
      selectedFlightIndex:
        existing.selectedFlightIndex != null
          ? existing.selectedFlightIndex
          : selectedResult.selectedFlightIndex ?? null,
      source: existing.source || sourceIfMissing,
      isDefault:
        existing.isDefault === true || (existing.source || sourceIfMissing) === "default_first",
    };
    return applySelectedFlightOptionToSelectedResult(selectedResult, withDefaults);
  }
  const built = buildSelectedFlightOptionFromUser({
    selectedResult,
    selectedFlight: selectedResult.linkedFlights,
    selectedFlightIndex: selectedResult.selectedFlightIndex,
    source: sourceIfMissing,
    linkedFlightsPool: selectedResult.linkedFlights,
  });
  if (!built) return selectedResult;
  return applySelectedFlightOptionToSelectedResult(selectedResult, built);
}

export function buildVacationFlightSelectionTracePayload(stage, selectedResult, selectedFlightOption, extra = {}) {
  const outbound = selectedFlightOption?.outboundFlight || null;
  const inbound = selectedFlightOption?.inboundFlight || null;
  return {
    stage,
    packageId:
      selectedResult?.dealId ??
      selectedResult?.ldsDealId ??
      selectedFlightOption?.ldsDealId ??
      null,
    selectedFlightOptionId: selectedFlightOption?.optionId ?? null,
    selectedFlightIndex: selectedFlightOption?.selectedFlightIndex ?? null,
    ldsDealId: selectedFlightOption?.ldsDealId ?? null,
    outboundFlightNumber: outbound?.flightNumber ?? null,
    inboundFlightNumber: inbound?.flightNumber ?? null,
    departureDateTime:
      selectedFlightOption?.departureDateTime ??
      toIsoDateTime(outbound?.depDate, outbound?.depTime),
    returnDateTime:
      selectedFlightOption?.returnDateTime ??
      toIsoDateTime(inbound?.depDate, inbound?.depTime),
    airline: selectedFlightOption?.airline ?? null,
    source: selectedFlightOption?.source ?? null,
    isDefault: selectedFlightOption?.isDefault === true,
    optionSignature: selectedFlightOption?.optionSignature ?? null,
    ...extra,
  };
}

export function logVacationFlightSelectionTrace(stage, selectedResult, selectedFlightOption, extra = {}) {
  if (!isDevRuntime()) return;
  try {
    console.info(
      TRACE_TAG,
      buildVacationFlightSelectionTracePayload(stage, selectedResult, selectedFlightOption, extra)
    );
  } catch {
    /* ignore trace failures */
  }
}

const FE_SELECTED_FLIGHT_SELECTION_TRACE = "[FE_SELECTED_FLIGHT_SELECTION_TRACE]";
const FE_SELECTED_FLIGHT_BOOK_PAYLOAD_TRACE = "[FE_SELECTED_FLIGHT_BOOK_PAYLOAD_TRACE]";
const FE_FLIGHT_DISPLAY_SOURCE_TRACE = "[FE_FLIGHT_DISPLAY_SOURCE_TRACE]";
const FE_FLIGHT_BOOKING_SOURCE_TRACE = "[FE_FLIGHT_BOOKING_SOURCE_TRACE]";

/** RCA audit fields for flight selection persistence (before/after compare). */
export function extractFeFlightSelectionAuditFields(selectedResultOrOption) {
  if (!selectedResultOrOption || typeof selectedResultOrOption !== "object") {
    return {
      selectedFlightIndex: null,
      optionSignature: null,
      source: null,
      isDefault: null,
    };
  }
  const nested = selectedResultOrOption.selectedFlightOptionFromUser;
  const opt =
    nested && typeof nested === "object" ? nested : selectedResultOrOption;
  const idx =
    opt?.selectedFlightIndex ??
    selectedResultOrOption?.selectedFlightIndex ??
    null;
  return {
    selectedFlightIndex: Number.isInteger(idx) ? idx : idx,
    optionSignature: opt?.optionSignature ?? null,
    source: opt?.source ?? null,
    isDefault: opt?.isDefault === true,
  };
}

/** RCA flags — report only; never synthesize default_first in trace paths. */
export function buildFeFlightSelectionRcaFlags(before, after, extra = {}) {
  const clickedIndex = extra.clickedIndex;
  const idxBefore = before?.selectedFlightIndex;
  const idxAfter = after?.selectedFlightIndex;
  const missingAfter = !Number.isInteger(idxAfter);
  const unchanged =
    idxBefore === idxAfter &&
    (idxBefore != null || idxAfter != null);
  const clickMismatch =
    Number.isInteger(clickedIndex) &&
    Number.isInteger(idxAfter) &&
    clickedIndex !== idxAfter;
  const clickNotApplied =
    Number.isInteger(clickedIndex) &&
    (missingAfter || clickMismatch);
  const defaultFirstDetected =
    after?.source === "default_first" || after?.isDefault === true;
  return {
    selectedFlightIndex_missing_after: missingAfter,
    selectedFlightIndex_unchanged: unchanged,
    selectedFlightIndex_click_mismatch: clickMismatch,
    selectedFlightIndex_click_not_applied: clickNotApplied,
    default_first_detected_after: defaultFirstDetected,
    default_first_applied_by_normalize: extra.default_first_applied_by_normalize === true,
    default_first_fallback_skipped_for_trace: true,
    trace_report_only: true,
    rca_anomaly:
      missingAfter ||
      clickNotApplied ||
      (Number.isInteger(clickedIndex) && unchanged && clickedIndex !== idxBefore),
  };
}

export function logFeSelectedFlightSelectionTrace(before, after, extra = {}) {
  if (!isDevRuntime()) return;
  try {
    const rca = buildFeFlightSelectionRcaFlags(before, after, extra);
    const payload = {
      selectedFlightIndex_before: before?.selectedFlightIndex ?? null,
      selectedFlightIndex_after: after?.selectedFlightIndex ?? null,
      optionSignature_before: before?.optionSignature ?? null,
      optionSignature_after: after?.optionSignature ?? null,
      source_before: before?.source ?? null,
      source_after: after?.source ?? null,
      isDefault_before: before?.isDefault === true,
      isDefault_after: after?.isDefault === true,
      ...rca,
      ...extra,
    };
    if (rca.rca_anomaly) {
      console.warn(FE_SELECTED_FLIGHT_SELECTION_TRACE, payload);
    } else {
      console.info(FE_SELECTED_FLIGHT_SELECTION_TRACE, payload);
    }
  } catch {
    /* ignore trace failures */
  }
}

export function logFeSelectedFlightBookPayloadTrace(payload, extra = {}) {
  if (!isDevRuntime()) return;
  const sf = payload?.selectedFlightOptionFromUser;
  const idx = payload?.selectedFlightIndex ?? sf?.selectedFlightIndex ?? null;
  const missingIndex = !Number.isInteger(idx);
  const missingSnapshot = !sf || typeof sf !== "object";
  const defaultFirstInPayload =
    sf?.source === "default_first" || sf?.isDefault === true;
  try {
    const tracePayload = {
      "payload.selectedFlightIndex": idx,
      "payload.selectedFlightOptionFromUser.optionSignature": sf?.optionSignature ?? null,
      "payload.selectedFlightOptionFromUser.source": sf?.source ?? null,
      "payload.selectedFlightOptionFromUser.isDefault": sf?.isDefault === true,
      selectedFlightIndex_missing: missingIndex,
      selectedFlightOptionFromUser_missing: missingSnapshot,
      default_first_in_payload: defaultFirstInPayload,
      used_default_first_fallback_in_code: extra.usedDefaultFirstFallback === true,
      default_first_fallback_skipped_for_trace: extra.default_first_fallback_skipped_for_trace !== false,
      trace_report_only: true,
      rca_anomaly:
        missingIndex ||
        missingSnapshot ||
        defaultFirstInPayload ||
        extra.usedDefaultFirstFallback === true,
      ...extra,
    };
    if (tracePayload.rca_anomaly) {
      console.warn(FE_SELECTED_FLIGHT_BOOK_PAYLOAD_TRACE, tracePayload);
    } else {
      console.info(FE_SELECTED_FLIGHT_BOOK_PAYLOAD_TRACE, tracePayload);
    }
  } catch {
    /* ignore trace failures */
  }
}

function legFlightNumber(leg) {
  if (!leg || typeof leg !== "object") return null;
  return leg.flightNumber ?? leg.fltNo ?? null;
}

function legDepDateOnly(leg) {
  if (!leg || typeof leg !== "object") return null;
  const raw = leg.depDate ?? leg.departureDate ?? null;
  if (raw == null) return null;
  const s = String(raw).trim();
  return s.split(" ")[0] || s;
}

/**
 * Same resolver used by PackageDetails / VacationPackagesFlightDetails display.
 */
export function resolvePackageFlightDisplayLinkedFlights(
  selectedResult,
  { fallbackLinkedFlights = null } = {}
) {
  if (selectedResult?.linkedFlights) {
    return {
      linkedFlights: selectedResult.linkedFlights,
      displaySource: "selectedResult.linkedFlights",
    };
  }
  if (selectedResult?.selectedFlightOptionFromUser?.linkedFlights) {
    return {
      linkedFlights: selectedResult.selectedFlightOptionFromUser.linkedFlights,
      displaySource: "selectedFlightOptionFromUser.linkedFlights",
    };
  }
  if (fallbackLinkedFlights) {
    return {
      linkedFlights: fallbackLinkedFlights,
      displaySource: "package.linkedFlights.fallback",
    };
  }
  return { linkedFlights: null, displaySource: "none" };
}

export function hasDisplayablePackageFlightLinkedFlights(linkedFlightsData) {
  const pairs = getDepRetFlightPairs(linkedFlightsData);
  return Boolean(pairs.dep?.leg && pairs.ret?.leg);
}

export function buildFeFlightSourceTracePayload({
  linkedFlightsData = null,
  displaySource = "unknown",
  selectedResult = null,
} = {}) {
  const pairs = getDepRetFlightPairs(linkedFlightsData);
  const depLeg =
    pairs.dep?.leg ??
    selectedResult?.selectedFlightOptionFromUser?.outboundFlight ??
    null;
  const retLeg =
    pairs.ret?.leg ??
    selectedResult?.selectedFlightOptionFromUser?.inboundFlight ??
    null;
  return {
    display_source: displaySource,
    display_optionSignature:
      buildFlightOptionSignature(linkedFlightsData) ||
      selectedResult?.selectedFlightOptionFromUser?.optionSignature ||
      null,
    display_outbound_flightNumber: legFlightNumber(depLeg),
    display_outbound_depDate: legDepDateOnly(depLeg),
    display_inbound_flightNumber: legFlightNumber(retLeg),
    display_inbound_depDate: legDepDateOnly(retLeg),
  };
}

export function logFeFlightDisplaySourceTrace(selectedResult, extra = {}) {
  if (!isDevRuntime()) return;
  try {
    const { linkedFlights, displaySource } = resolvePackageFlightDisplayLinkedFlights(
      selectedResult,
      extra
    );
    const payload = {
      ...buildFeFlightSourceTracePayload({
        linkedFlightsData: linkedFlights,
        displaySource,
        selectedResult,
      }),
      ...extra,
    };
    console.info(FE_FLIGHT_DISPLAY_SOURCE_TRACE, payload);
  } catch {
    /* ignore trace failures */
  }
}

export function logFeFlightBookingSourceTrace(
  selectedResult,
  selectedFlightOptionFromUser,
  extra = {}
) {
  if (!isDevRuntime()) return;
  try {
    const displayResolved = resolvePackageFlightDisplayLinkedFlights(selectedResult, extra);
    const displayTrace = buildFeFlightSourceTracePayload({
      linkedFlightsData: displayResolved.linkedFlights,
      displaySource: displayResolved.displaySource,
      selectedResult,
    });
    const bookingTrace = buildFeFlightSourceTracePayload({
      linkedFlightsData: selectedFlightOptionFromUser?.linkedFlights ?? null,
      displaySource:
        extra.booking_source || "selectedFlightOptionFromUser.linkedFlights",
      selectedResult,
    });
    const displaySig = normalizeFlightSelectionSignature(
      displayTrace.display_optionSignature || ""
    );
    const bookingSig = normalizeFlightSelectionSignature(
      bookingTrace.display_optionSignature || ""
    );
    const payload = {
      ...bookingTrace,
      display_source: displayTrace.display_source,
      display_optionSignature: displayTrace.display_optionSignature,
      display_outbound_flightNumber: displayTrace.display_outbound_flightNumber,
      display_outbound_depDate: displayTrace.display_outbound_depDate,
      display_inbound_flightNumber: displayTrace.display_inbound_flightNumber,
      display_inbound_depDate: displayTrace.display_inbound_depDate,
      booking_source: bookingTrace.display_source,
      booking_optionSignature: bookingTrace.display_optionSignature,
      booking_outbound_flightNumber: bookingTrace.display_outbound_flightNumber,
      booking_outbound_depDate: bookingTrace.display_outbound_depDate,
      booking_inbound_flightNumber: bookingTrace.display_inbound_flightNumber,
      booking_inbound_depDate: bookingTrace.display_inbound_depDate,
      signatures_match: Boolean(displaySig && bookingSig && displaySig === bookingSig),
      ...extra,
    };
    if (!payload.signatures_match) {
      console.warn(FE_FLIGHT_BOOKING_SOURCE_TRACE, payload);
    } else {
      console.info(FE_FLIGHT_BOOKING_SOURCE_TRACE, payload);
    }
  } catch {
    /* ignore trace failures */
  }
}

/**
 * Booking SSOT: use the same linkedFlights object the package screen displays.
 * Skips default_first when a displayable flight exists on screen.
 */
export function resolvePackageFlightBookingSelection(
  selectedResult,
  { fallbackLinkedFlights = null } = {}
) {
  const displayResolved = resolvePackageFlightDisplayLinkedFlights(selectedResult, {
    fallbackLinkedFlights,
  });
  const { linkedFlights: displayLinkedFlights, displaySource } = displayResolved;
  const hasDisplayableFlight = hasDisplayablePackageFlightLinkedFlights(displayLinkedFlights);
  const existing = selectedResult?.selectedFlightOptionFromUser;

  if (hasDisplayableFlight && displayLinkedFlights) {
    const displaySig = normalizeFlightSelectionSignature(
      buildFlightOptionSignature(displayLinkedFlights) || ""
    );
    const existingSig = existing?.optionSignature
      ? normalizeFlightSelectionSignature(existing.optionSignature)
      : existing?.linkedFlights
        ? normalizeFlightSelectionSignature(
            buildFlightOptionSignature(existing.linkedFlights) || ""
          )
        : "";

    if (existing?.linkedFlights && displaySig && existingSig && displaySig === existingSig) {
      return {
        selectedFlightOptionFromUser: existing,
        bookingSource: "selectedFlightOptionFromUser.matched_display",
        displaySource,
      };
    }

    const rebuilt = buildSelectedFlightOptionFromUser({
      selectedResult,
      selectedFlight: displayLinkedFlights,
      selectedFlightIndex:
        selectedResult?.selectedFlightIndex ??
        existing?.selectedFlightIndex ??
        null,
      source:
        existing?.source === "user_selected" ? "user_selected" : "display_ssot",
      linkedFlightsPool: displayLinkedFlights,
    });
    if (rebuilt) {
      return {
        selectedFlightOptionFromUser: rebuilt,
        bookingSource: displaySource,
        displaySource,
      };
    }
  }

  if (existing?.linkedFlights) {
    return {
      selectedFlightOptionFromUser: existing,
      bookingSource: "selectedFlightOptionFromUser",
      displaySource,
    };
  }

  const fallback = buildSelectedFlightOptionFromUser({
    selectedResult,
    selectedFlight: displayLinkedFlights ?? selectedResult?.linkedFlights,
    selectedFlightIndex: selectedResult?.selectedFlightIndex,
    source: "default_first",
    linkedFlightsPool: displayLinkedFlights ?? selectedResult?.linkedFlights,
  });
  return {
    selectedFlightOptionFromUser: fallback,
    bookingSource: fallback ? "default_first" : "unresolved",
    displaySource,
  };
}

const FE_FLIGHT_OPTION_TOKEN_TRACE = "[LDS_FE_FLIGHT_OPTION_TOKEN_TRACE]";

function simpleTokenHash(token) {
  if (token == null || String(token).trim() === "") return null;
  const s = String(token);
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return `h${h.toString(16)}`;
}

/**
 * Preserve per-option ldsDealId from getPackageFlightLinks; fallback to global only when missing.
 */
export function preserveFlightOptionLdsDealIdsFromApi(linkedFlightsOptions, globalLdsDealId, sourceComponent = "") {
  if (!Array.isArray(linkedFlightsOptions)) return linkedFlightsOptions;
  const global = globalLdsDealId != null ? String(globalLdsDealId).trim() : "";
  return linkedFlightsOptions.map((lf, optionIndex) => {
    if (!lf || typeof lf !== "object") return lf;
    const originalOptionLdsDealId =
      lf.ldsDealId ?? lf.dealId ?? lf.LdsDealId ?? null;
    const originalOptionLdsDealIdExists = Boolean(
      originalOptionLdsDealId != null && String(originalOptionLdsDealId).trim() !== ""
    );
    const usedFallback = !originalOptionLdsDealIdExists && Boolean(global);
    const selectedToken = originalOptionLdsDealIdExists
      ? String(originalOptionLdsDealId).trim()
      : global || null;
    if (isDevRuntime()) {
      try {
        console.info(FE_FLIGHT_OPTION_TOKEN_TRACE, {
          optionIndex,
          optionSignature: buildFlightOptionSignature(lf),
          originalOptionLdsDealIdExists,
          globalLdsDealIdExists: Boolean(global),
          selectedTokenHash: simpleTokenHash(selectedToken),
          usedFallback,
          sourceComponent,
        });
      } catch {
        /* ignore */
      }
    }
    return selectedToken ? { ...lf, ldsDealId: selectedToken } : { ...lf };
  });
}

/**
 * Sync selected flight token into passengersinfo room slots (FlightsHotel LDS SOAP SSOT).
 * Single-room only: multi-room orders must keep per-room combo ldsDealId from room selection.
 */
export function syncSelectedFlightLdsDealIdToPassengersInfo(roomsAndPaxInfo, selectedFlightOption) {
  const selectedToken = pickLdsOpaqueDealToken(selectedFlightOption?.ldsDealId);
  if (!Array.isArray(roomsAndPaxInfo) || !selectedToken) {
    return roomsAndPaxInfo;
  }
  if (isHrInternalBase64JsonDealToken(selectedFlightOption?.ldsDealId)) {
    return roomsAndPaxInfo;
  }
  if (roomsAndPaxInfo.length >= 2) {
    if (isDevRuntime()) {
      try {
        console.info("[LDS_SELECTED_FLIGHT_SSOT_TRACE]", {
          stage: "fe_sync_room_ldsDealId_skipped_multi_room",
          roomCount: roomsAndPaxInfo.length,
        });
      } catch {
        /* ignore */
      }
    }
    return roomsAndPaxInfo;
  }
  const token = selectedToken;
  if (!token) return roomsAndPaxInfo;

  const next = roomsAndPaxInfo.map((slot) => {
    if (!slot || typeof slot !== "object") return slot;
    const room = slot.room && typeof slot.room === "object" ? { ...slot.room } : {};
    const roomOpaque = pickLdsOpaqueDealToken(room.ldsDealId);
    if (roomOpaque && roomOpaque !== token) {
      return slot;
    }
    room.ldsDealId = token;
    if (!isHrInternalBase64JsonDealToken(room.dealId)) {
      room.dealId = room.dealId || token;
    }
    return { ...slot, room };
  });

  if (isDevRuntime()) {
    try {
      console.info("[LDS_SELECTED_FLIGHT_SSOT_TRACE]", {
        stage: "fe_sync_room_ldsDealId",
        selectedTokenHash: simpleTokenHash(token),
        roomCount: next.length,
      });
    } catch {
      /* ignore */
    }
  }
  return next;
}
